import { useCallback, useRef } from "react";

/**
 * `useInputHistory` — terminal-style Up/Down recall for a command-ish text input (the
 * FoundryVTT chat-history convention). Session-local only: memory lives in a `useRef`, never
 * persisted, never localStorage — a reload starts empty. The state transitions
 * ({@link pushEntry}, {@link navigateUp}, {@link navigateDown}) are deliberately plain
 * functions with no React or DOM dependency, so they're both the whole test surface and
 * reusable by any other command-ish input without dragging in COMMS.app.
 *
 * Contract for a caller (see `CommsApp.tsx`'s `comms-input` form for the wiring):
 *   - `push(text)` on every successful send (after `postChat`, before clearing the draft).
 *   - `up(draft)` / `down(draft)` on the input's `onKeyDown` for `"ArrowUp"`/`"ArrowDown"`,
 *     passing the input's *current* draft each time; `setDraft` to whatever they return.
 */
export type InputHistoryState = {
  /** Sent entries, oldest first (`entries[entries.length - 1]` is the most recent send). */
  entries: string[];
  /**
   * Recall position: `null` means "not navigating" (the live draft is showing). Otherwise a
   * distance-from-the-end into `entries` — `0` is the newest entry, `entries.length - 1` the
   * oldest.
   */
  index: number | null;
  /** The draft that was in progress when Up was first pressed; restored when Down walks past the newest entry. */
  stashedDraft: string;
};

export const EMPTY_INPUT_HISTORY: InputHistoryState = { entries: [], index: null, stashedDraft: "" };

/** `entries[index]` read via distance-from-the-end (`index` 0 = newest). */
function entryAt(entries: string[], index: number): string {
  return entries[entries.length - 1 - index]!;
}

/**
 * Push-on-send: skip a blank/whitespace-only send (nothing worth recalling), and dedupe an
 * *immediate* repeat (sending the same text twice in a row doesn't grow the history). Any push
 * cancels an in-progress recall — the caller has just cleared its draft, so there's nothing to
 * stash back to.
 */
export function pushEntry(state: InputHistoryState, text: string): InputHistoryState {
  const trimmed = text.trim();
  if (trimmed === "") return state;
  const last = state.entries[state.entries.length - 1];
  const entries = last === trimmed ? state.entries : [...state.entries, trimmed];
  return { entries, index: null, stashedDraft: "" };
}

/**
 * Up: first press stashes `currentDraft` and recalls the newest entry; each subsequent press
 * steps one entry further into the past, clamped at the oldest (no wrap). A no-op (returns
 * `currentDraft` unchanged) when there's no history yet.
 */
export function navigateUp(state: InputHistoryState, currentDraft: string): { state: InputHistoryState; draft: string } {
  if (state.entries.length === 0) return { state, draft: currentDraft };
  if (state.index === null) {
    return { state: { ...state, index: 0, stashedDraft: currentDraft }, draft: entryAt(state.entries, 0) };
  }
  const index = Math.min(state.index + 1, state.entries.length - 1);
  return { state: { ...state, index }, draft: entryAt(state.entries, index) };
}

/**
 * Down: steps one entry back toward the present; walking down past the newest entry restores the
 * stashed draft and exits recall (`index` back to `null`). A no-op when not currently navigating
 * (returns `currentDraft` unchanged) — there's nothing below the live draft.
 */
export function navigateDown(state: InputHistoryState, currentDraft: string): { state: InputHistoryState; draft: string } {
  if (state.index === null) return { state, draft: currentDraft };
  if (state.index === 0) {
    return { state: { ...state, index: null, stashedDraft: "" }, draft: state.stashedDraft };
  }
  const index = state.index - 1;
  return { state: { ...state, index }, draft: entryAt(state.entries, index) };
}

/** The thin React wrapper: a `useRef`-backed session store around the three pure transforms above. */
export function useInputHistory(): {
  push: (text: string) => void;
  up: (currentDraft: string) => string;
  down: (currentDraft: string) => string;
} {
  const ref = useRef<InputHistoryState>(EMPTY_INPUT_HISTORY);

  const push = useCallback((text: string) => {
    ref.current = pushEntry(ref.current, text);
  }, []);

  const up = useCallback((currentDraft: string) => {
    const { state, draft } = navigateUp(ref.current, currentDraft);
    ref.current = state;
    return draft;
  }, []);

  const down = useCallback((currentDraft: string) => {
    const { state, draft } = navigateDown(ref.current, currentDraft);
    ref.current = state;
    return draft;
  }, []);

  return { push, up, down };
}
